import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Accordion, AccordionGroup } from '../../shared/Accordion'
import StepCard from '../shared/StepCard'

function BenefitCard({ icon, title, description }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 hover:border-primary-200 hover:shadow-sm transition-all">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center">
          {icon}
        </div>
        <div>
          <h4 className="font-medium text-gray-900 mb-1">{title}</h4>
          <p className="text-sm text-gray-600">{description}</p>
        </div>
      </div>
    </div>
  )
}

function BioPageIntro({ showFAQ = true, compact = false }) {
  const { t } = useTranslation('ecommerce')

  const faqSections = [
    {
      key: 'basics',
      title: t('bioPageFaq.basics.title'),
      items: [
        { key: 'whatIs', question: t('bioPageFaq.basics.whatIs.question'), answer: t('bioPageFaq.basics.whatIs.answer') },
        { key: 'whyNeed', question: t('bioPageFaq.basics.whyNeed.question'), answer: t('bioPageFaq.basics.whyNeed.answer') },
        { key: 'howAccess', question: t('bioPageFaq.basics.howAccess.question'), answer: t('bioPageFaq.basics.howAccess.answer') },
      ],
    },
    {
      key: 'customization',
      title: t('bioPageFaq.customization.title'),
      items: [
        { key: 'changeDesign', question: t('bioPageFaq.customization.changeDesign.question'), answer: t('bioPageFaq.customization.changeDesign.answer') },
        { key: 'whichProducts', question: t('bioPageFaq.customization.whichProducts.question'), answer: t('bioPageFaq.customization.whichProducts.answer') },
        { key: 'productLinks', question: t('bioPageFaq.customization.productLinks.question'), answer: t('bioPageFaq.customization.productLinks.answer') },
      ],
    },
    {
      key: 'technical',
      title: t('bioPageFaq.technical.title'),
      items: [
        { key: 'ownDomain', question: t('bioPageFaq.technical.ownDomain.question'), answer: t('bioPageFaq.technical.ownDomain.answer') },
        { key: 'tracking', question: t('bioPageFaq.technical.tracking.question'), answer: t('bioPageFaq.technical.tracking.answer') },
        { key: 'legalRequirements', question: t('bioPageFaq.technical.legalRequirements.question'), answer: t('bioPageFaq.technical.legalRequirements.answer') },
      ],
    },
    {
      key: 'publishing',
      title: t('bioPageFaq.publishing.title'),
      items: [
        { key: 'whenVisible', question: t('bioPageFaq.publishing.whenVisible.question'), answer: t('bioPageFaq.publishing.whenVisible.answer') },
        { key: 'updateProducts', question: t('bioPageFaq.publishing.updateProducts.question'), answer: t('bioPageFaq.publishing.updateProducts.answer') },
        { key: 'multipleBioPages', question: t('bioPageFaq.publishing.multipleBioPages.question'), answer: t('bioPageFaq.publishing.multipleBioPages.answer') },
      ],
    },
  ]

  return (
    <div className="space-y-10">
      {/* Intro Section */}
      <section className="space-y-6">
        <div className="text-center max-w-3xl mx-auto">
          <h2 className={`font-bold text-gray-900 ${compact ? 'text-xl' : 'text-2xl'}`}>
            {t('bioPageIntro.headline')}
          </h2>
          <p className="mt-3 text-gray-600">
            {t('bioPageIntro.description')}
          </p>
          <p className="mt-3 text-gray-700 text-sm">
            {t('bioPageIntro.valueProposition')}
          </p>
        </div>

        {/* How it works */}
        <div className={`bg-gradient-to-br from-primary-50 to-white rounded-xl ${compact ? 'p-5' : 'p-6 md:p-8'}`}>
          <h3 className="font-semibold text-gray-900 mb-6 text-center">
            {t('bioPageIntro.howItWorks.title')}
          </h3>

          <div className="max-w-lg mx-auto">
            <StepCard
              number="1"
              title={t('bioPageIntro.howItWorks.step1.title')}
              description={t('bioPageIntro.howItWorks.step1.description')}
            />
            <StepCard
              number="2"
              title={t('bioPageIntro.howItWorks.step2.title')}
              description={t('bioPageIntro.howItWorks.step2.description')}
            />
            <StepCard
              number="3"
              title={t('bioPageIntro.howItWorks.step3.title')}
              description={t('bioPageIntro.howItWorks.step3.description')}
            />
          </div>
        </div>

        {/* Benefits */}
        {!compact && (
          <div>
            <h3 className="font-semibold text-gray-900 mb-4 text-center">
              {t('bioPageIntro.benefits.title')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <BenefitCard
                icon={
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                }
                title={t('bioPageIntro.benefits.noLinkChanges.title')}
                description={t('bioPageIntro.benefits.noLinkChanges.description')}
              />
              <BenefitCard
                icon={
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                }
                title={t('bioPageIntro.benefits.directSales.title')}
                description={t('bioPageIntro.benefits.directSales.description')}
              />
              <BenefitCard
                icon={
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                }
                title={t('bioPageIntro.benefits.trackClicks.title')}
                description={t('bioPageIntro.benefits.trackClicks.description')}
              />
              <BenefitCard
                icon={
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                }
                title={t('bioPageIntro.benefits.legalCompliance.title')}
                description={t('bioPageIntro.benefits.legalCompliance.description')}
              />
            </div>
          </div>
        )}

        {/* Result */}
        <div className="text-center">
          <p className="inline-block bg-white px-5 py-3 rounded-full text-gray-700 text-sm font-medium shadow-sm border border-gray-100">
            {t('bioPageIntro.result')}
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      {showFAQ && (
        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">
            {t('bioPageFaq.title')}
          </h2>

          <div className="max-w-3xl mx-auto bg-gray-50 rounded-2xl p-8">
            <div className="space-y-8">
              {faqSections.map((section) => (
                <div key={section.key}>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">{section.title}</h3>
                  <AccordionGroup exclusive className="space-y-3">
                    {section.items.map((item) => (
                      <Accordion key={item.key} title={item.question} variant="neutral">
                        <p className="text-gray-600 text-sm leading-relaxed">{item.answer}</p>
                      </Accordion>
                    ))}
                  </AccordionGroup>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  )
}

BenefitCard.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
}

BioPageIntro.propTypes = {
  showFAQ: PropTypes.bool,
  compact: PropTypes.bool,
}

export default BioPageIntro
